import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Money, CreditCard, Gift, Trophy, Bank, ClockCounterClockwise, Check, Trash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { CashierNumericKeypad } from "./CashierNumericKeypad";
import { changeDue, formatMoney, remainingDue, tendersTotal } from "./utils";
import type { CashierLabels, CashierPaymentMethod, CashierTender } from "./types";

const TENDER_ICONS: Partial<Record<CashierPaymentMethod, React.ComponentType<{ className?: string; weight?: "regular" | "fill" }>>> = {
  cash: Money as never,
  card: CreditCard as never,
  gift_card: Gift as never,
  loyalty_points: Trophy as never,
  transfer: Bank as never,
  on_account: ClockCounterClockwise as never,
};

export interface CashierTenderPanelProps {
  isOpen: boolean;
  onClose: () => void;
  totalMinor: number;
  availableTenders: CashierPaymentMethod[];
  onCommit: (tenders: CashierTender[]) => Promise<void> | void;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierTenderPanel({
  isOpen, onClose, totalMinor, availableTenders, onCommit, labels, locale = "tr",
}: CashierTenderPanelProps) {
  const [tenders, setTenders] = React.useState<CashierTender[]>([]);
  const [activeMethod, setActiveMethod] = React.useState<CashierPaymentMethod>(availableTenders[0] ?? "cash");
  const [amountStr, setAmountStr] = React.useState("");
  const [reference, setReference] = React.useState("");
  const [committing, setCommitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setTenders([]);
      setAmountStr(String(Math.round(totalMinor / 100)));
      setActiveMethod(availableTenders[0] ?? "cash");
      setReference("");
      setSuccess(false);
    }
  }, [isOpen, totalMinor, availableTenders]);

  const remaining = remainingDue(totalMinor, tenders);
  const change = changeDue(totalMinor, tenders);
  const paid = tendersTotal(tenders);
  const canCommit = paid >= totalMinor && tenders.length > 0;

  const addTender = () => {
    const amt = Math.round(Number(amountStr || "0") * 100);
    if (amt <= 0) return;
    setTenders((t) => [...t, { method: activeMethod, amountMinor: amt, reference: reference || null }]);
    const nextRemaining = Math.max(0, totalMinor - (paid + amt));
    setAmountStr(nextRemaining > 0 ? String(Math.round(nextRemaining / 100)) : "");
    setReference("");
  };

  const commit = async () => {
    setCommitting(true);
    try {
      await onCommit(tenders);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1100);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-label={labels.tender.title}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-x-0 bottom-0 z-50 bg-biz-bg rounded-t-[14px] biz-shadow-lg max-h-[92vh] overflow-auto"
          >
            <div className="mx-auto h-1.5 w-12 rounded-full bg-biz-border mt-3" />
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-biz-foreground">{labels.tender.title}</h2>
                <p className="text-xs text-biz-foreground-muted mt-0.5">
                  {labels.cart.total}: <span className="font-mono-num font-bold text-biz-foreground">{formatMoney(totalMinor, labels.currencyPrefix, locale)}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-10 w-10 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted biz-focus-ring transition-colors flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 pb-6 max-w-3xl mx-auto grid gap-4 lg:grid-cols-[1fr_360px]">
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {availableTenders.map((m) => {
                    const Icon = TENDER_ICONS[m] ?? Money;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setActiveMethod(m)}
                        aria-pressed={activeMethod === m}
                        className={cn(
                          "min-h-[64px] rounded-biz-btn px-3 py-2 text-sm font-semibold inline-flex flex-col items-center justify-center gap-1",
                          "transition-colors biz-focus-ring",
                          activeMethod === m
                            ? "bg-biz-primary text-biz-primary-foreground biz-shadow-sm"
                            : "bg-biz-surface-elevated border border-biz-border text-biz-foreground hover:bg-biz-muted",
                        )}
                      >
                        <Icon className="h-5 w-5" weight="fill" />
                        {tenderLabel(m, labels)}
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-biz-card border border-biz-border bg-biz-surface p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-biz-foreground-muted">
                      {activeMethod === "cash" ? labels.tender.cashReceived : labels.tender.addTender}
                    </span>
                    {activeMethod === "cash" && (
                      <span className="text-xs text-biz-foreground-muted">
                        {labels.tender.changeDue}:{" "}
                        <span className="font-mono-num font-bold text-biz-success tabular-nums">
                          {formatMoney(Math.max(0, Math.round(Number(amountStr || "0") * 100) - totalMinor + paid), labels.currencyPrefix, locale)}
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="text-right font-mono-num text-3xl font-bold text-biz-foreground tabular-nums px-3 py-2 bg-biz-bg rounded-biz-btn">
                    {labels.currencyPrefix}{amountStr || "0"}
                  </div>
                  {(activeMethod === "card" || activeMethod === "transfer" || activeMethod === "gift_card") && (
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder={activeMethod === "card" ? labels.tender.last4 : labels.tender.reference}
                      className="h-11 w-full rounded-biz-btn border border-biz-border bg-biz-surface-elevated px-3 text-sm font-mono-num biz-focus-ring"
                    />
                  )}
                  <CashierNumericKeypad
                    value={amountStr}
                    onChange={setAmountStr}
                    onSubmit={() => addTender()}
                    mode="amount"
                    labels={labels}
                    submitLabel={labels.tender.addTender}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-3 space-y-2">
                  <Row label={labels.cart.total} value={formatMoney(totalMinor, labels.currencyPrefix, locale)} bold />
                  <div className="h-px bg-biz-border" />
                  <Row label={labels.tender.remaining} value={formatMoney(remaining, labels.currencyPrefix, locale)} highlight={remaining > 0 ? "warn" : "ok"} />
                  {change > 0 && (
                    <Row label={labels.tender.overPayment} value={formatMoney(change, labels.currencyPrefix, locale)} highlight="ok" />
                  )}
                </div>

                <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-2 min-h-[120px]">
                  <AnimatePresence initial={false}>
                    {tenders.length === 0 ? (
                      <p className="text-xs text-biz-foreground-subtle text-center py-6">
                        {labels.tender.addTender}
                      </p>
                    ) : tenders.map((t, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.16 }}
                        className="flex items-center gap-2 px-2 py-2 rounded-biz-btn hover:bg-biz-muted/40"
                      >
                        <span className="text-xs font-semibold text-biz-foreground-muted flex-1">
                          {tenderLabel(t.method, labels)}{t.reference ? ` · ${t.reference}` : ""}
                        </span>
                        <span className="font-mono-num text-sm font-bold text-biz-foreground tabular-nums">
                          {formatMoney(t.amountMinor, labels.currencyPrefix, locale)}
                        </span>
                        <button
                          type="button"
                          aria-label="remove"
                          onClick={() => setTenders((arr) => arr.filter((_, idx) => idx !== i))}
                          className="h-8 w-8 rounded-biz-btn text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle transition-colors flex items-center justify-center"
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                <button
                  type="button"
                  onClick={commit}
                  disabled={!canCommit || committing}
                  className={cn(
                    "w-full min-h-[56px] rounded-biz-btn font-bold text-base inline-flex items-center justify-center gap-2",
                    "bg-biz-success text-biz-success-foreground biz-shadow-md",
                    "hover:opacity-95 transition-opacity biz-focus-ring",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  {success ? <Check className="h-5 w-5" /> : null}
                  {success ? labels.tender.commitSuccess : labels.tender.commit}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Row({ label, value, bold, highlight }: { label: string; value: string; bold?: boolean; highlight?: "ok" | "warn" }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={cn("text-sm", bold ? "text-biz-foreground font-semibold" : "text-biz-foreground-muted")}>{label}</span>
      <span
        className={cn(
          "font-mono-num tabular-nums",
          bold ? "text-base font-bold text-biz-foreground" : "text-sm",
          highlight === "ok" && "text-biz-success font-semibold",
          highlight === "warn" && "text-biz-warning font-semibold",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function tenderLabel(m: CashierPaymentMethod, labels: CashierLabels): string {
  switch (m) {
    case "cash": return labels.tender.cash;
    case "card": return labels.tender.card;
    case "gift_card": return labels.tender.giftCard;
    case "loyalty_points": return labels.tender.loyaltyPoints;
    case "transfer": return labels.tender.transfer;
    case "on_account": return labels.tender.onAccount;
  }
}
